import '../assets/main.css';

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import * as Sentry from '@sentry/vue';
import { isAxiosError } from 'axios';

import { createGtag } from 'vue-gtag';
import App from './App.vue';
import vuetifyPlugin from './providers/plugin/vuetify';
import { createAppRouter } from './providers/router';
import { initializeAuthInterceptor, authStore } from './providers/auth';
import { initIntlTelInput } from './providers/intlTelInput';
import { setupApplicationServerErrorInterceptor } from '@/shared/api/applicationServerErrorInterceptor';

const app = createApp(App);
const router = createAppRouter('web');

// Sentry初期化（本番環境のみ有効）
// VITE_APP_ENV はデプロイ時に環境ごと（development/staging/production）に設定される。
// dev/stg もビルドは本番モード（import.meta.env.PROD=true）になるため、PROD ではなく
// VITE_APP_ENV で判定し、本番環境のみイベントを送信する。ローカル開発では未設定のため無効。
Sentry.init({
    app,
    dsn: 'https://a93637511797cdf02a18c85a3f54a784@o4510316735430656.ingest.us.sentry.io/4510718616535040',
    enabled: import.meta.env.VITE_APP_ENV === 'production',
    environment: import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
        const error = hint.originalException;

        // 401エラー: セッション/トークン期限切れによる想定内のエラー（インターセプタでリフレッシュ済み）
        if (isAxiosError(error) && error.response?.status === 401) {
            return null;
        }

        // 非ErrorオブジェクトのUnhandledRejection（サードパーティライブラリ由来のノイズ）
        if (!(error instanceof Error) && event.exception?.values?.[0]?.mechanism?.type === 'onunhandledrejection') {
            return null;
        }

        // DuckDuckGoブラウザ等のWKWebViewブリッジ注入スクリプト由来のノイズ（アプリ非依存）
        // Errorオブジェクトでないケースがあるため、文字列・event.messageも参照する
        const message = error instanceof Error ? error.message : typeof error === 'string' ? error : event.message;
        if (message?.includes('WKWebView API client did not respond to this postMessage')) {
            return null;
        }

        // WeChatブラウザ(iOS)が注入するJSブリッジ由来のノイズ（アプリ非依存）
        // 例: undefined is not an object (evaluating 'window.weixinPostMessageHandlers.weixinDispatchMessage.postMessage')
        if (message?.includes('weixinPostMessageHandlers') || message?.includes('WeixinJSBridge')) {
            return null;
        }

        // Outlook(Safe Links)の埋め込みブラウザが注入するスクリプト由来のノイズ（アプリ非依存）
        // 例: Object Not Found Matching Id:1, MethodName:update, ParamCount:4
        if (message?.includes('Object Not Found Matching Id')) {
            return null;
        }

        if (error instanceof Error) {
            // AbortError: ユーザーのページ遷移によるリクエストキャンセル
            if (error.name === 'AbortError') {
                return null;
            }

            // Axios Network Error: 一時的なネットワーク障害
            if (error.message === 'Network Error') {
                return null;
            }

            // デプロイ後のチャンク読み込みエラー（router.onErrorでリロード対応済み）
            if (
                error.message.includes('Failed to fetch dynamically imported module') ||
                error.message.includes('Importing a module script failed') ||
                error.message.includes('Unable to preload CSS') ||
                error.message.includes('is not a valid JavaScript MIME type')
            ) {
                return null;
            }

            // 循環import起因のTDZエラー（致命的でないと判断し送信対象外）
            // 例: ReferenceError: Cannot access 'default' before initialization
            if (error instanceof ReferenceError && error.message.includes('before initialization')) {
                return null;
            }

            // Google翻訳によるDOM書き換えとVueのリアクティビティ衝突で発生する無限再帰
            if (error.name === 'RangeError' && error.message.includes('Maximum call stack size exceeded')) {
                return null;
            }
        }

        return event;
    },
});

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
app.use(pinia);

// authGuardより先にトークンを復元する必要がある
authStore.initializeAuth();
initializeAuthInterceptor();
setupApplicationServerErrorInterceptor(router);

app.use(router);
app.use(vuetifyPlugin);

// GA4トラッキング（本番環境 + 測定ID存在時のみ有効）
// page_viewはrouter afterEachで手動送信するため、自動送信を無効化
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
if (import.meta.env.PROD && GA_MEASUREMENT_ID) {
    app.use(
        createGtag({
            tagId: GA_MEASUREMENT_ID,
            config: { send_page_view: false },
        })
    );
}

// intl-tel-input ユーティリティの初期化（バリデーション機能に必要）
await initIntlTelInput();

// dev:mock でvite サーバーを実行したらmswサーバーも動く
if (import.meta.env.MODE === 'mock') {
    const { worker } = await import('@/mock/browser');
    await worker.start(); // MSWの準備完了を待つ
}

app.mount('#app');
